import { useState, useCallback } from 'react';
export function useMonthlyReport() {
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [snapshots, setSnapshots] = useState([]);
    const [snapshotsLoading, setSnapshotsLoading] = useState(false);
    const [rateAudit, setRateAudit] = useState([]);
    const generateReport = useCallback(async (projectId, month, groupBy) => {
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ month, groupBy: groupBy.join(',') });
            const res = await fetch(`/api/monthly-reports/${projectId}/generate?${params}`, {
                credentials: 'include',
            });
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                throw new Error(body.error || `Failed (${res.status})`);
            }
            const data = await res.json();
            setReportData(data);
            return data;
        }
        catch (err) {
            const msg = err instanceof Error ? err.message : 'Failed to generate report';
            setError(msg);
            return null;
        }
        finally {
            setLoading(false);
        }
    }, []);
    const saveSnapshot = useCallback(async (projectId, month, data, title, notes) => {
        try {
            const res = await fetch(`/api/monthly-reports/${projectId}/snapshot`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ month, reportData: data, title, notes }),
            });
            if (!res.ok)
                throw new Error('Failed to save snapshot');
            const result = await res.json();
            return result.id;
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save snapshot');
            return null;
        }
    }, []);
    const loadSnapshot = useCallback(async (snapshotId) => {
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(`/api/monthly-reports/snapshots/${snapshotId}`, {
                credentials: 'include',
            });
            if (!res.ok)
                throw new Error('Snapshot not found');
            const snapshot = await res.json();
            setReportData(snapshot.reportData);
            return snapshot;
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load snapshot');
            return null;
        }
        finally {
            setLoading(false);
        }
    }, []);
    const listSnapshots = useCallback(async (projectId) => {
        setSnapshotsLoading(true);
        try {
            const res = await fetch(`/api/monthly-reports/${projectId}/snapshots?limit=50`, {
                credentials: 'include',
            });
            if (!res.ok)
                throw new Error('Failed to load snapshots');
            const data = await res.json();
            setSnapshots(data.snapshots || []);
        }
        catch {
            setSnapshots([]);
        }
        finally {
            setSnapshotsLoading(false);
        }
    }, []);
    const deleteSnapshot = useCallback(async (snapshotId) => {
        try {
            const res = await fetch(`/api/monthly-reports/snapshots/${snapshotId}`, {
                method: 'DELETE',
                credentials: 'include',
            });
            if (!res.ok)
                throw new Error('Failed to delete snapshot');
            setSnapshots(prev => prev.filter(s => s.id !== snapshotId));
            return true;
        }
        catch {
            return false;
        }
    }, []);
    const exportReport = useCallback(async (projectId, month, groupBy, format) => {
        try {
            const params = new URLSearchParams({
                month,
                groupBy: groupBy.join(','),
                format,
            });
            const res = await fetch(`/api/monthly-reports/${projectId}/export?${params}`, {
                credentials: 'include',
            });
            if (!res.ok)
                throw new Error('Export failed');
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `monthly-report-${month}.${format}`;
            a.click();
            URL.revokeObjectURL(url);
            return true;
        }
        catch {
            setError('Export failed');
            return false;
        }
    }, []);
    const setClientRate = useCallback(async (projectId, rate, notes) => {
        try {
            const res = await fetch(`/api/monthly-reports/${projectId}/client-rate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ rate, notes }),
            });
            if (!res.ok)
                throw new Error('Failed to set client rate');
            return true;
        }
        catch {
            setError('Failed to set client rate');
            return false;
        }
    }, []);
    const getClientRateAudit = useCallback(async (projectId) => {
        try {
            const res = await fetch(`/api/monthly-reports/${projectId}/client-rate/audit`, {
                credentials: 'include',
            });
            if (!res.ok)
                throw new Error('Failed to get audit');
            const data = await res.json();
            setRateAudit(data.audit || []);
        }
        catch {
            setRateAudit([]);
        }
    }, []);
    return {
        reportData,
        loading,
        error,
        snapshots,
        snapshotsLoading,
        rateAudit,
        generateReport,
        saveSnapshot,
        loadSnapshot,
        listSnapshots,
        deleteSnapshot,
        exportReport,
        setClientRate,
        getClientRateAudit,
        setReportData,
        setError,
    };
}
//# sourceMappingURL=useMonthlyReport.js.map